import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, withCtx as _withCtx, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "page-content" }
const _hoisted_2 = { class: "py-3" }
const _hoisted_3 = { class: "flex flex-wrap gap-2 mt-3" }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { key: 2 }
const _hoisted_7 = { class: "p-3" }
const _hoisted_8 = { class: "field mb-2" }
const _hoisted_9 = { class: "field mb-2" }
const _hoisted_10 = { class: "field" }
const _hoisted_11 = {
  key: 3,
  class: "flex-1"
}
const _hoisted_12 = {
  class: "mr-2 pt-1",
  style: {"float":"left"}
}
const _hoisted_13 = { key: 4 }
const _hoisted_14 = { key: 5 }
const _hoisted_15 = ["textContent"]
const _hoisted_16 = ["textContent"]
const _hoisted_17 = { class: "w-20rem white-space-normal" }
const _hoisted_18 = {
  key: 0,
  textContent: 'N/A'
}
const _hoisted_19 = ["textContent"]
const _hoisted_20 = ["textContent"]
const _hoisted_21 = ["textContent"]

import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { DataTablePageEvent, DataTableSortEvent } from 'primevue/datatable';
import { DropdownChangeEvent } from 'primevue/dropdown';
import MultiSelect, { MultiSelectChangeEvent } from 'primevue/multiselect';
import OverlayPanel from 'primevue/overlaypanel';
import SelectButton from 'primevue/selectbutton';
import { can, useRouting, useDateTime, useRequestCancel, useIsEqual } from '@/composables';
import { useEventReportStore, useReportStore } from '@/store';
import { EventReportDownloadFileType } from '@/types';
import { PERMISSIONS, EMIT } from '@/enum';
import ReportSubjectTitle from '@/views/Reports/components/ReportSubjectTitle.vue';

/**
 * Define a routing instance for the utility.
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'EventsReport',
  emits: [EMIT.QUERY_PARAMS],
  setup(__props, { emit: __emit }) {

const $routing = useRouting();

/**
 * Define the store instances for event report data.
 */
const pageLoading = ref(true);

/**
 * Define all available events that can be emitted back to the parent.
 */
const emit = __emit;

/**
 * Define the store instances for event report data.
 */
const reportStore = useReportStore();
const eventReportStore = useEventReportStore();
const {
  filters: {
    value: { eventTypes, therapyTypes, disciplines, attendees },
  },
} = storeToRefs(eventReportStore);

/**
 * The filter overlay reference object used to toggle the overlay.
 */
const filtersOverlay = ref();
const rmsReportToggleValue = ref('Events');
const eventReportTypes = computed(() => {
  const isRsm = reportStore.filters.organizations.isRsm;
  const availableReports = ['Events'];

  if (isRsm && can([PERMISSIONS.REPORTS_RSM_INDEX])) {
    availableReports.push('RSM Events');
  }

  return availableReports;
});

/**
 * Initialize everything required to build out the component.
 */
onMounted(() => init());

/**
 * cancel any and all reuests before unmounting
 */
onBeforeUnmount(() => {
  useRequestCancel().requestSaverAbortAll();
  $routing.router.push({ query: {} });
  eventReportStore.clearStoreFilters();
  pageLoading.value = true;
});

watch(
  () => $routing.route.query,
  async (newValue, oldValue) => {
    if (pageLoading.value) return;

    /* eslint-disable */
    const ignoredKeys = ['page', 'per_page', 'order', 'order_by'];
    const newFilteredValue = Object.fromEntries(
      Object.entries(newValue).filter(([key, _]) => !ignoredKeys.includes(key)),
    );
    const oldFilteredValue = Object.fromEntries(
      Object.entries(oldValue).filter(([key, _]) => !ignoredKeys.includes(key)),
    );

    const doNewRequest = !useIsEqual(newFilteredValue, oldFilteredValue);
    eventReportStore.queryParams = $routing.route.query;

    await defineFilters();

    if (doNewRequest) {
      await getEventsCount();
    }

    await doEventsReport();

    if (doNewRequest) {
      await getAttendees();
    }
  },
);

/**
 * Initialize the event report default values and data.
 */
const init = async () => {
  await defineFilters();
  await eventReportStore.getEventTypes();
  await eventReportStore.getDisciplines();
  await eventReportStore.getTherapyResultTypes();

  if (!reportStore.referrer) {
    reportStore.referrer = $routing.route.name;
  }

  if (pageLoading.value) {
    await getEventsCount();
    await doEventsReport();
    await getAttendees();
  }

  pageLoading.value = false;
  eventReportStore.clearStoreFilters();
};

/**
 * Execute the events report request based on the provided query param filters.
 */
const doEventsReport = async () => {
  const query = $routing.route.query;
  const filters = eventReportStore.defineFilters(query);
  await eventReportStore.getEventsReport(filters);
};

const getAttendees = async () => {
  await eventReportStore.getAttendees();
};

const getEventsCount = async () => {
  const query = $routing.route.query;
  const filters = eventReportStore.defineFilters(query);
  await eventReportStore.getEventsCount(filters);
};

/**
 * Define the filters based on the query params available on page load.
 */
const defineFilters = async () => {
  const query = $routing.route.query;
  eventReportStore.filters.order = query.order || eventReportStore.filters.order;
  eventReportStore.filters.order_by = query.order_by || eventReportStore.filters.order_by;
  eventReportStore.filters.page = parseInt(query.page) || eventReportStore.filters.page;
  eventReportStore.filters.per_page = parseInt(query.per_page) || eventReportStore.filters.per_page;

  const attendees: number[] = [];
  if (query.attendees) {
    query.attendees
      .toString()
      .split(',')
      .forEach((item) => attendees.push(+item));
  }
  eventReportStore.filters.attendees.selected = query.attendees ? attendees : null;

  const eventTypes: number[] = [];
  if (query.event_types) {
    query.event_types
      .toString()
      .split(',')
      .forEach((item) => eventTypes.push(item));
  }
  eventReportStore.filters.eventTypes.selected = query.event_types ? eventTypes : null;

  const disciplines: number[] = [];
  if (query.disciplines) {
    query.disciplines
      .toString()
      .split(',')
      .forEach((item) => disciplines.push(+item));
  }
  eventReportStore.filters.disciplines.selected = query.disciplines ? disciplines : null;

  const therapyTypes: number[] = [];
  if (query.therapy_result_types) {
    query.therapy_result_types
      .toString()
      .split(',')
      .forEach((item) => therapyTypes.push(+item));
  }
  eventReportStore.filters.therapyTypes.selected = query.therapy_result_types ? therapyTypes : null;

  const organizations: number[] = [];
  if (query.organizations) {
    query.organizations
      .toString()
      .split(',')
      .forEach((item) => organizations.push(+item));
  }

  eventReportStore.filters.organizations.selected = query.organizations ? organizations : null;
  if (query.is_rsm) {
    rmsReportToggleValue.value = query.is_rsm == '1' ? 'RSM Events' : 'Events';
    eventReportStore.events.rsmToggle = rmsReportToggleValue.value;
    eventReportStore.filters.is_rsm = Number(query.is_rsm);
  }

  // Add back the query params for the report once it regains focus again.
  if (eventReportStore.queryParams) {
    $routing.addQueryParams(eventReportStore.queryParams);
  } else {
    emit(EMIT.QUERY_PARAMS);
  }
};

/**
 * Handle the filtering of results based on the selected attendees.
 */
const onAttendeeChange = (event: MultiSelectChangeEvent) => {
  if (event.value.length) {
    $routing.addQueryParam('attendees', event.value.join());
  } else {
    $routing.removeQueryParam('attendees');
  }
};

/**
 * Handle the filtering of results based on the selected event_types.
 */
const onEventTypeChange = (event: MultiSelectChangeEvent) => {
  if (event.value.length) {
    $routing.addQueryParam('event_types', event.value.join());
  } else {
    $routing.removeQueryParam('event_types');
  }
};

/**
 * Handle the filtering of results based on the selected therapy_result_types.
 */
const onTherapyTypeChange = (event: MultiSelectChangeEvent) => {
  if (event.value.length) {
    $routing.addQueryParam('therapy_result_types', event.value.join());
  } else {
    $routing.removeQueryParam('therapy_result_types');
  }
};

/**
 * Handle the filtering of results based on the selected disciplines.
 */
const onDisciplineChange = (event: MultiSelectChangeEvent) => {
  if (event.value.length) {
    $routing.addQueryParam('disciplines', event.value.join());
  } else {
    $routing.removeQueryParam('disciplines');
  }
};

/**
 * Handle the RSM event filtering
 */
const onRsmToggleEvent = () => {
  eventReportStore.$patch({
    events: {
      rsmToggle: rmsReportToggleValue.value,
    },
  });

  if (can([PERMISSIONS.REPORTS_RSM_INDEX])) {
    const isRsm = eventReportStore.events.rsmToggle === 'RSM Events' ? '1' : '0';
    $routing.addQueryParam('is_rsm', isRsm);
    reportStore.is_rsm = Number(isRsm);
  }
};

/**
 * Handle the pagination of the request data.
 */
const onPage = (event: DataTablePageEvent) => {
  $routing.addQueryParams({ per_page: event.rows, page: event.page + 1 });
};

/**
 * Handle the ordering of the request data.
 */
const onSort = (event: DataTableSortEvent) => {
  const order = event.sortOrder === 1 ? 'asc' : 'desc';
  $routing.addQueryParams({ order, order_by: event.sortField });
};

/**
 * Handle the toggling of the available filter overlay.
 */
const onDisplayFilterOverlay = (event: DropdownChangeEvent) => filtersOverlay.value.toggle(event);

/**
 * Handle the export action for the specific report type.
 */
const onExportReport = async (fileType: EventReportDownloadFileType) => {
  await eventReportStore
    .downloadEventsReport($routing.route.query, fileType)
    .then((response) => {
      if (response.status === 202) {
        eventReportStore.doToast(
          'You will receive an email with a link to download the exported data.',
          'Your export has started',
        );
      }
      eventReportStore.events.downloading = false;
    })
    .catch((response) => {
      if (response.status === 422) {
        eventReportStore.doToast('Select only one organization to generate a report.', 'Error exporting report', true);
      }
      eventReportStore.events.downloading = false;
    });
};

/**
 * Define the tooltip text for the export buttons.
 */
const tooltipExportText = (fileType: EventReportDownloadFileType) =>
  eventReportStore.canDownload ? `Download as ${fileType}` : `Email as ${fileType}`;

/**
 * Define the goal type and stats.
 */
const defineGoal = (goal) =>
  goal.total_trials > 0 ? `Trials (${goal.trials}/${goal.total_trials}) ` : `Percentage (${goal.percentage}%) `;

/**
 * Define the goal description and determine whether or not to display a shortened version.
 */
const defineGoalDescription = (goal, tooltip: boolean) => {
  const description = goal.treatment_goal?.description;

  if (tooltip && description) return description;
  return description && description.length > 50 ? `${description.substring(0, 50)} (...)` : description;
};

/**
 * Define the full goal information to be displayed within the tooltip.
 */
const defineGoalTooltip = (goal) => `${defineGoal(goal)} ${defineGoalDescription(goal, true)}`;

return (_ctx: any,_cache: any) => {
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_InputSwitch = _resolveComponent("InputSwitch")!
  const _component_Column = _resolveComponent("Column")!
  const _component_DataTable = _resolveComponent("DataTable")!
  const _component_Card = _resolveComponent("Card")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_Card, null, {
      content: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(ReportSubjectTitle, {
            title: "Events",
            name: _unref(reportStore).organizations,
            dates: _unref(reportStore).filters.dates
          }, null, 8, ["name", "dates"]),
          _createElementVNode("div", _hoisted_3, [
            (_unref(can)([_unref(PERMISSIONS).REPORTS_RSM_INDEX]))
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createVNode(_unref(SelectButton), {
                    modelValue: rmsReportToggleValue.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((rmsReportToggleValue).value = $event)),
                    options: eventReportTypes.value,
                    onChange: onRsmToggleEvent
                  }, null, 8, ["modelValue", "options"])
                ]))
              : _createCommentVNode("", true),
            (_unref(can)([_unref(PERMISSIONS).REPORTS_FILTER_SERVICE_PROVIDERS]))
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createVNode(_unref(MultiSelect), {
                    id: "reports_events_attendee",
                    modelValue: _unref(attendees).selected,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(attendees).selected) = $event)),
                    options: _unref(attendees).options,
                    loading: _unref(attendees).loading,
                    filter: true,
                    "reset-filter-on-hide": true,
                    "show-toggle-all": false,
                    class: "w-full",
                    "option-label": "name",
                    "option-value": "value",
                    placeholder: "All Attendees",
                    display: "chip",
                    onChange: onAttendeeChange
                  }, null, 8, ["modelValue", "options", "loading"])
                ]))
              : _createCommentVNode("", true),
            (_unref(can)([_unref(PERMISSIONS).REPORTS_FILTER_VIEW]))
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createVNode(_component_AppButton, {
                    type: "secondary",
                    text: "Filters",
                    "icon-left": "filter_list",
                    onClick: onDisplayFilterOverlay
                  }),
                  _createVNode(_unref(OverlayPanel), {
                    ref_key: "filtersOverlay",
                    ref: filtersOverlay
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_7, [
                        _createElementVNode("div", _hoisted_8, [
                          _cache[9] || (_cache[9] = _createElementVNode("label", { for: "username" }, "Event Type", -1)),
                          _createVNode(_unref(MultiSelect), {
                            id: "reports_events_event_type",
                            modelValue: _unref(eventTypes).selected,
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(eventTypes).selected) = $event)),
                            options: _unref(eventTypes).options,
                            loading: _unref(eventTypes).loading,
                            filter: true,
                            "reset-filter-on-hide": true,
                            "show-toggle-all": false,
                            class: "w-full",
                            "option-label": "name",
                            "option-value": "value",
                            placeholder: "All Event Types",
                            display: "chip",
                            onChange: onEventTypeChange
                          }, null, 8, ["modelValue", "options", "loading"])
                        ]),
                        _createElementVNode("div", _hoisted_9, [
                          _cache[10] || (_cache[10] = _createElementVNode("label", { for: "username" }, "Attendance Type", -1)),
                          _createVNode(_unref(MultiSelect), {
                            id: "reports_events_therapy_type",
                            modelValue: _unref(therapyTypes).selected,
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(therapyTypes).selected) = $event)),
                            options: _unref(therapyTypes).options,
                            loading: _unref(therapyTypes).loading,
                            filter: true,
                            "reset-filter-on-hide": true,
                            "show-toggle-all": false,
                            class: "w-full",
                            "option-label": "name",
                            "option-value": "value",
                            placeholder: "All Attendance Types",
                            display: "chip",
                            onChange: onTherapyTypeChange
                          }, null, 8, ["modelValue", "options", "loading"])
                        ]),
                        _createElementVNode("div", _hoisted_10, [
                          _cache[11] || (_cache[11] = _createElementVNode("label", { for: "username" }, "Discipline", -1)),
                          _createVNode(_unref(MultiSelect), {
                            id: "reports_events_discipline",
                            modelValue: _unref(disciplines).selected,
                            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(disciplines).selected) = $event)),
                            options: _unref(disciplines).options,
                            loading: _unref(disciplines).loading,
                            filter: true,
                            "reset-filter-on-hide": true,
                            "show-toggle-all": false,
                            class: "w-full",
                            "option-label": "name",
                            "option-value": "value",
                            placeholder: "All Disciplines",
                            display: "chip",
                            onChange: onDisciplineChange
                          }, null, 8, ["modelValue", "options", "loading"])
                        ])
                      ])
                    ]),
                    _: 1
                  }, 512)
                ]))
              : _createCommentVNode("", true),
            (_unref(can)([_unref(PERMISSIONS).REPORTS_EVENT_SHOW_ALL_COLUMNS_VIEW]))
              ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                  _createElementVNode("div", _hoisted_12, [
                    _createVNode(_component_InputSwitch, {
                      modelValue: _unref(eventReportStore).filters.allColumns,
                      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((_unref(eventReportStore).filters.allColumns) = $event))
                    }, null, 8, ["modelValue"])
                  ]),
                  _cache[12] || (_cache[12] = _createElementVNode("div", {
                    class: "w-4rem text-gray-600 font-normal line-height-1",
                    style: {"float":"left"}
                  }, [
                    _createElementVNode("label", { class: "text-xs mt-1" }, "Show All Columns")
                  ], -1))
                ]))
              : _createCommentVNode("", true),
            (_unref(can)([_unref(PERMISSIONS).REPORTS_EVENTS_DOWNLOAD_CSV]))
              ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                  _withDirectives(_createVNode(_component_AppButton, {
                    disabled: !_unref(eventReportStore).events.total,
                    loading: _unref(eventReportStore).events.downloading,
                    type: "secondary",
                    text: "CSV",
                    "icon-left": "system_update_alt",
                    onClick: _cache[6] || (_cache[6] = ($event: any) => (onExportReport('csv')))
                  }, null, 8, ["disabled", "loading"]), [
                    [
                      _directive_tooltip,
                      tooltipExportText('CSV'),
                      void 0,
                      { bottom: true }
                    ]
                  ])
                ]))
              : _createCommentVNode("", true),
            (_unref(can)([_unref(PERMISSIONS).REPORTS_EVENTS_DOWNLOAD_XSLX]))
              ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                  _withDirectives(_createVNode(_component_AppButton, {
                    disabled: !_unref(eventReportStore).events.total,
                    loading: _unref(eventReportStore).events.downloading,
                    type: "secondary",
                    text: "XLSX",
                    "icon-left": "system_update_alt",
                    onClick: _cache[7] || (_cache[7] = ($event: any) => (onExportReport('xlsx')))
                  }, null, 8, ["disabled", "loading"]), [
                    [
                      _directive_tooltip,
                      tooltipExportText('XLSX'),
                      void 0,
                      { bottom: true }
                    ]
                  ])
                ]))
              : _createCommentVNode("", true)
          ])
        ]),
        _createVNode(_component_DataTable, {
          filters: _unref(eventReportStore).filters,
          "onUpdate:filters": _cache[8] || (_cache[8] = ($event: any) => ((_unref(eventReportStore).filters) = $event)),
          "filter-display": "menu",
          value: _unref(eventReportStore).events.data,
          paginator: true,
          rows: _unref(eventReportStore).filters.per_page,
          "total-records": _unref(eventReportStore).events.total,
          "data-key": "id",
          "row-hover": true,
          lazy: true,
          "loading-icon": "logo-spinner",
          loading: _unref(eventReportStore).events.loading,
          "rows-per-page-options": [15, 25, 30],
          "paginator-template": "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown",
          "current-page-report-template": "Showing {first} to {last} of {totalRecords}",
          "responsive-layout": "scroll",
          "resizable-columns": true,
          "column-resize-mode": "fit",
          onPage: onPage,
          onSort: onSort
        }, {
          empty: _withCtx(() => [...(_cache[13] || (_cache[13] = [
            _createTextVNode("No events found", -1)
          ]))]),
          loading: _withCtx(() => [...(_cache[14] || (_cache[14] = [
            _createTextVNode("Loading event data. Please wait.", -1)
          ]))]),
          default: _withCtx(() => [
            (_unref(eventReportStore).showAllColumns)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createVNode(_component_Column, {
                    field: "date",
                    header: "Date",
                    sortable: ""
                  }, {
                    body: _withCtx(({ data }) => [
                      _createElementVNode("span", {
                        textContent: _toDisplayString(_unref(useDateTime)().formatIntlDate(data.date))
                      }, null, 8, _hoisted_15)
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_Column, {
                    field: "time",
                    header: "Time"
                  }),
                  _createVNode(_component_Column, {
                    field: "event_type",
                    header: "Event Type"
                  }),
                  _createVNode(_component_Column, {
                    field: "session_type",
                    header: "Session Type"
                  }),
                  _createVNode(_component_Column, {
                    field: "organization",
                    header: "Organization"
                  }, {
                    body: _withCtx(({ data }) => [
                      _createElementVNode("span", {
                        textContent: _toDisplayString(data.organization.length ? data.organization.join(',') : 'N/A')
                      }, null, 8, _hoisted_16)
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_Column, {
                    field: "clinician.display_name",
                    header: "Service Provider"
                  }),
                  _createVNode(_component_Column, {
                    field: "attendee.display_name",
                    header: "Attendee"
                  }),
                  _createVNode(_component_Column, {
                    field: "attendee.externalAssociation.external_id",
                    header: "SSID"
                  }),
                  _createVNode(_component_Column, {
                    field: "therapy_result_type",
                    header: "Attendance Type"
                  }),
                  _createVNode(_component_Column, {
                    field: "group",
                    header: "Group",
                    "body-class": "text-center"
                  }),
                  _createVNode(_component_Column, {
                    field: "planned_minutes",
                    header: "Planned Minutes",
                    "body-class": "text-center"
                  }),
                  _createVNode(_component_Column, {
                    field: "actual_minutes",
                    header: "Actual Minutes",
                    "body-class": "text-center"
                  }),
                  _createVNode(_component_Column, {
                    field: "documented",
                    header: "Documented"
                  }),
                  _createVNode(_component_Column, {
                    field: "notes",
                    header: "Notes"
                  }, {
                    body: _withCtx(({ data }) => [
                      _createElementVNode("div", _hoisted_17, _toDisplayString(data.notes), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_Column, {
                    field: "goals",
                    header: "Goals"
                  }, {
                    body: _withCtx(({ data }) => [
                      (data.goals === 'N/A' || !data.goals.length)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_18))
                        : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(data.goals, (item, key) => {
                            return _withDirectives((_openBlock(), _createElementBlock("div", {
                              key: key,
                              class: "mb-3"
                            }, [
                              _createElementVNode("span", {
                                class: "text-blue-600",
                                textContent: _toDisplayString(defineGoal(item))
                              }, null, 8, _hoisted_19),
                              _createElementVNode("span", {
                                textContent: _toDisplayString(defineGoalDescription(item, false))
                              }, null, 8, _hoisted_20)
                            ])), [
                              [
                                _directive_tooltip,
                                defineGoalTooltip(item),
                                void 0,
                                { left: true }
                              ]
                            ])
                          }), 128))
                    ]),
                    _: 1
                  })
                ], 64))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createVNode(_component_Column, {
                    field: "date",
                    header: "Date",
                    "header-style": "width: 200px",
                    sortable: ""
                  }, {
                    body: _withCtx(({ data }) => [
                      _createElementVNode("span", {
                        textContent: _toDisplayString(_unref(useDateTime)().formatIntlDate(data.date))
                      }, null, 8, _hoisted_21)
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_Column, {
                    field: "event_type",
                    header: "Event Type",
                    "header-style": "width: 300px"
                  }),
                  _createVNode(_component_Column, {
                    field: "attendee.display_name",
                    header: "Attendee"
                  })
                ], 64))
          ]),
          _: 1
        }, 8, ["filters", "value", "rows", "total-records", "loading"])
      ]),
      _: 1
    })
  ]))
}
}

})